var _a;
import { __assign, __read, __rest } from "tslib";
import { systemToComponent } from './react-urx';
import * as u from './urx';
import React from 'react';
import useIsomorphicLayoutEffect from './hooks/useIsomorphicLayoutEffect';
import useChangedListContentsSizes from './hooks/useChangedChildSizes';
import useScrollTop from './hooks/useScrollTop';
import useSize from './hooks/useSize';
import { listSystem } from './listSystem';
import { positionStickyCssValue } from './utils/positionStickyCssValue';
import useWindowViewportRectRef from './hooks/useWindowViewportRect';
import { correctItemSize } from './utils/correctItemSize';
import { VirtuosoMockContext } from './utils/context';
export function identity(value) {
    return value;
}
var listComponentPropsSystem = /*#__PURE__*/ u.system(function () {
    var itemContent = u.statefulStream(function (index) { return "Item " + index; });
    var context = u.statefulStream(null);
    var groupContent = u.statefulStream(function (index) { return "Group " + index; });
    var components = u.statefulStream({});
    var computeItemKey = u.statefulStream(identity);
    var headerFooterTag = u.statefulStream('div');
    var scrollerRef = u.statefulStream(u.noop);
    var distinctProp = function (propName, defaultValue) {
        if (defaultValue === void 0) { defaultValue = null; }
        return u.statefulStreamFromEmitter(u.pipe(components, u.map(function (components) { return components[propName]; }), u.distinctUntilChanged()), defaultValue);
    };
    return {
        context: context,
        itemContent: itemContent,
        groupContent: groupContent,
        components: components,
        computeItemKey: computeItemKey,
        headerFooterTag: headerFooterTag,
        scrollerRef: scrollerRef,
        FooterComponent: distinctProp('Footer'),
        HeaderComponent: distinctProp('Header'),
        TopItemListComponent: distinctProp('TopItemList'),
        ListComponent: distinctProp('List', 'div'),
        ItemComponent: distinctProp('Item', 'div'),
        GroupComponent: distinctProp('Group', 'div'),
        ScrollerComponent: distinctProp('Scroller', 'div'),
        EmptyPlaceholder: distinctProp('EmptyPlaceholder'),
        ScrollSeekPlaceholder: distinctProp('ScrollSeekPlaceholder'),
    };
});
var combinedSystem = /*#__PURE__*/ u.system(function (_a) {
    var _b = __read(_a, 2), listSystem = _b[0], propsSystem = _b[1];
    return __assign(__assign({}, listSystem), propsSystem);
}, u.tup(listSystem, listComponentPropsSystem));
var DefaultScrollSeekPlaceholder = function (_a) {
    var height = _a.height;
    return React.createElement("div", { style: { height: height } });
};
var GROUP_STYLE = { position: positionStickyCssValue(), zIndex: 1, overflowAnchor: 'none' };
var ITEM_STYLE = { overflowAnchor: 'none' };
var Items = /*#__PURE__*/ React.memo(function VirtuosoItems(_a) {
    var _b = _a.showTopList, showTopList = _b === void 0 ? false : _b;
    var listState = useEmitterValue('listState');
    var sizeRanges = usePublisher('sizeRanges');
    var useWindowScroll = useEmitterValue('useWindowScroll');
    var customScrollParent = useEmitterValue('customScrollParent');
    var windowScrollContainerStateCallback = usePublisher('windowScrollContainerState');
    var _scrollContainerStateCallback = usePublisher('scrollContainerState');
    var scrollContainerStateCallback = customScrollParent || useWindowScroll ? windowScrollContainerStateCallback : _scrollContainerStateCallback;
    var itemContent = useEmitterValue('itemContent');
    var context = useEmitterValue('context');
    var groupContent = useEmitterValue('groupContent');
    var trackItemSizes = useEmitterValue('trackItemSizes');
    var itemSize = useEmitterValue('itemSize');
    var log = useEmitterValue('log');
    var listGap = usePublisher('gap');
    var callbackRef = useChangedListContentsSizes(sizeRanges, itemSize, trackItemSizes, showTopList ? u.noop : scrollContainerStateCallback, log, listGap, customScrollParent).callbackRef;
    var _c = __read(React.useState(0), 2), deviation = _c[0], setDeviation = _c[1];
    useEmitter('deviation', function (value) {
        if (deviation !== value) {
            // ref.current!.style.marginTop = `${value}px`
            setDeviation(value);
        }
    });
    var EmptyPlaceholder = useEmitterValue('EmptyPlaceholder');
    var ScrollSeekPlaceholder = useEmitterValue('ScrollSeekPlaceholder') || DefaultScrollSeekPlaceholder;
    var ListComponent = useEmitterValue('ListComponent');
    var ItemComponent = useEmitterValue('ItemComponent');
    var GroupComponent = useEmitterValue('GroupComponent');
    var computeItemKey = useEmitterValue('computeItemKey');
    var isSeeking = useEmitterValue('isSeeking');
    var hasGroups = useEmitterValue('groupIndices').length > 0;
    var paddingTopAddition = useEmitterValue('paddingTopAddition');
    var scrolledToInitialItem = useEmitterValue('scrolledToInitialItem');
    var containerStyle = showTopList
        ? {}
        : __assign({ boxSizing: 'border-box', paddingTop: listState.offsetTop + paddingTopAddition, paddingBottom: listState.offsetBottom, marginTop: deviation }, (scrolledToInitialItem ? {} : { visibility: 'hidden' }));
    if (!showTopList && listState.totalCount === 0 && EmptyPlaceholder) {
        return React.createElement(EmptyPlaceholder, contextPropIfNotDomElement(EmptyPlaceholder, context));
    }
    return React.createElement(ListComponent, __assign(__assign({}, contextPropIfNotDomElement(ListComponent, context)), { ref: callbackRef, style: containerStyle, 'data-test-id': showTopList ? 'virtuoso-top-item-list' : 'virtuoso-item-list' }), (showTopList ? listState.topItems : listState.items).map(function (item) {
        var index = item.originalIndex;
        var key = computeItemKey(index + listState.firstItemIndex, item.data, context);
        if (isSeeking) {
            return React.createElement(ScrollSeekPlaceholder, __assign(__assign(__assign({}, contextPropIfNotDomElement(ScrollSeekPlaceholder, context)), { key: key, index: item.index, height: item.size, type: item.type || 'item' }), (item.type === 'group' ? {} : { groupIndex: item.groupIndex })));
        }
        if (item.type === 'group') {
            return React.createElement(GroupComponent, __assign(__assign({}, contextPropIfNotDomElement(GroupComponent, context)), { key: key, 'data-index': index, 'data-known-size': item.size, 'data-item-index': item.index, style: GROUP_STYLE }), groupContent(item.index, context));
        }
        else {
            return React.createElement(ItemComponent, __assign(__assign(__assign({}, contextPropIfNotDomElement(ItemComponent, context)), itemPropIfNotDomElement(ItemComponent, item.data)), { key: key, 'data-index': index, 'data-known-size': item.size, 'data-item-index': item.index, 'data-item-group-index': item.groupIndex, style: ITEM_STYLE }), hasGroups
                ? itemContent(item.index, item.groupIndex, item.data, context)
                : itemContent(item.index, item.data, context));
        }
    }));
});
export var scrollerStyle = {
    height: '100%',
    outline: 'none',
    overflowY: 'auto',
    position: 'relative',
    WebkitOverflowScrolling: 'touch',
};
export var viewportStyle = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
};
var topItemListStyle = {
    width: '100%',
    position: positionStickyCssValue(),
    top: 0,
    zIndex: 1,
};
export function contextPropIfNotDomElement(element, context) {
    if (typeof element === 'string') {
        return undefined;
    }
    return { context: context };
}
export function itemPropIfNotDomElement(element, item) {
    return { item: typeof element === 'string' ? undefined : item };
}
var Header = React.memo(function VirtuosoHeader() {
    var Header = useEmitterValue('HeaderComponent');
    var headerHeight = usePublisher('headerHeight');
    var headerFooterTag = useEmitterValue('headerFooterTag');
    var ref = useSize(function (el) { return headerHeight(correctItemSize(el, 'height')); });
    var context = useEmitterValue('context');
    return Header
        ? React.createElement(headerFooterTag, { ref: ref }, React.createElement(Header, contextPropIfNotDomElement(Header, context)))
        : null;
});
var Footer = React.memo(function VirtuosoFooter() {
    var Footer = useEmitterValue('FooterComponent');
    var footerHeight = usePublisher('footerHeight');
    var headerFooterTag = useEmitterValue('headerFooterTag');
    var ref = useSize(function (el) { return footerHeight(correctItemSize(el, 'height')); });
    var context = useEmitterValue('context');
    return Footer
        ? React.createElement(headerFooterTag, { ref: ref }, React.createElement(Footer, contextPropIfNotDomElement(Footer, context)))
        : null;
});
export function buildScroller(_a) {
    var usePublisher = _a.usePublisher, useEmitter = _a.useEmitter, useEmitterValue = _a.useEmitterValue;
    var Scroller = React.memo(function VirtuosoScroller(_a) {
        var style = _a.style, children = _a.children, props = __rest(_a, ["style", "children"]);
        var scrollContainerStateCallback = usePublisher('scrollContainerState');
        var ScrollerComponent = useEmitterValue('ScrollerComponent');
        var smoothScrollTargetReached = usePublisher('smoothScrollTargetReached');
        var scrollerRefCallback = useEmitterValue('scrollerRef');
        var context = useEmitterValue('context');
        var _b = useScrollTop(scrollContainerStateCallback, smoothScrollTargetReached, ScrollerComponent, scrollerRefCallback), scrollerRef = _b.scrollerRef, scrollByCallback = _b.scrollByCallback, scrollToCallback = _b.scrollToCallback;
        useEmitter('scrollTo', scrollToCallback);
        useEmitter('scrollBy', scrollByCallback);
        return React.createElement(ScrollerComponent, __assign(__assign({ ref: scrollerRef, style: __assign(__assign({}, scrollerStyle), style), 'data-test-id': 'virtuoso-scroller', 'data-virtuoso-scroller': true, tabIndex: 0 }, props), contextPropIfNotDomElement(ScrollerComponent, context)), children);
    });
    return Scroller;
}
export function buildWindowScroller(_a) {
    var usePublisher = _a.usePublisher, useEmitter = _a.useEmitter, useEmitterValue = _a.useEmitterValue;
    var Scroller = React.memo(function VirtuosoWindowScroller(_a) {
        var style = _a.style, children = _a.children, props = __rest(_a, ["style", "children"]);
        var scrollContainerStateCallback = usePublisher('windowScrollContainerState');
        var ScrollerComponent = useEmitterValue('ScrollerComponent');
        var smoothScrollTargetReached = usePublisher('smoothScrollTargetReached');
        var totalListHeight = useEmitterValue('totalListHeight');
        var deviation = useEmitterValue('deviation');
        var customScrollParent = useEmitterValue('customScrollParent');
        var context = useEmitterValue('context');
        var _b = useScrollTop(scrollContainerStateCallback, smoothScrollTargetReached, ScrollerComponent, u.noop, customScrollParent), scrollerRef = _b.scrollerRef, scrollByCallback = _b.scrollByCallback, scrollToCallback = _b.scrollToCallback;
        useIsomorphicLayoutEffect(function () {
            scrollerRef.current = customScrollParent ? customScrollParent : window;
            return function () {
                scrollerRef.current = null;
            };
        }, [scrollerRef, customScrollParent]);
        useEmitter('windowScrollTo', scrollToCallback);
        useEmitter('scrollBy', scrollByCallback);
        return React.createElement(ScrollerComponent, __assign(__assign({ style: __assign(__assign({ position: 'relative' }, style), (totalListHeight !== 0 ? { height: totalListHeight + deviation } : {})), 'data-virtuoso-scroller': true }, props), contextPropIfNotDomElement(ScrollerComponent, context)), children);
    });
    return Scroller;
}
var Viewport = function (_a) {
    var children = _a.children;
    var ctx = React.useContext(VirtuosoMockContext);
    var viewportHeight = usePublisher('viewportHeight');
    var fixedItemHeight = usePublisher('fixedItemHeight');
    var viewportRef = useSize(u.compose(viewportHeight, function (el) { return correctItemSize(el, 'height'); }));
    React.useEffect(function () {
        if (ctx) {
            viewportHeight(ctx.viewportHeight);
            fixedItemHeight(ctx.itemHeight);
        }
    }, [ctx, viewportHeight, fixedItemHeight]);
    return (React.createElement("div", { style: viewportStyle, ref: viewportRef, "data-viewport-type": "element" }, children));
};
var WindowViewport = function (_a) {
    var children = _a.children;
    var ctx = React.useContext(VirtuosoMockContext);
    var windowViewportRect = usePublisher('windowViewportRect');
    var fixedItemHeight = usePublisher('fixedItemHeight');
    var customScrollParent = useEmitterValue('customScrollParent');
    var viewportRef = useWindowViewportRectRef(windowViewportRect, customScrollParent);
    React.useEffect(function () {
        if (ctx) {
            fixedItemHeight(ctx.itemHeight);
            windowViewportRect({ offsetTop: 0, visibleHeight: ctx.viewportHeight, visibleWidth: 100 });
        }
    }, [ctx, windowViewportRect, fixedItemHeight]);
    return (React.createElement("div", { ref: viewportRef, style: viewportStyle, "data-viewport-type": "window" }, children));
};
var TopItemListContainer = function (_a) {
    var children = _a.children;
    var TopItemList = useEmitterValue('TopItemListComponent');
    var headerHeight = useEmitterValue('headerHeight');
    var style = __assign(__assign({}, topItemListStyle), { marginTop: headerHeight + "px" });
    var context = useEmitterValue('context');
    return React.createElement(TopItemList || 'div', { style: style, context: context }, children);
};
var ListRoot = React.memo(function VirtuosoRoot(props) {
    var useWindowScroll = useEmitterValue('useWindowScroll');
    var showTopList = useEmitterValue('topItemsIndexes').length > 0;
    var customScrollParent = useEmitterValue('customScrollParent');
    var TheScroller = customScrollParent || useWindowScroll ? WindowScroller : Scroller;
    var TheViewport = customScrollParent || useWindowScroll ? WindowViewport : Viewport;
    return (React.createElement(TheScroller, __assign({}, props),
        showTopList && (React.createElement(TopItemListContainer, null,
            React.createElement(Items, { showTopList: true }))),
        React.createElement(TheViewport, null,
            React.createElement(Header, null),
            React.createElement(Items, null),
            React.createElement(Footer, null))));
});
export var List = (_a = systemToComponent(combinedSystem, {
    required: {},
    optional: {
        restoreStateFrom: 'restoreStateFrom',
        context: 'context',
        followOutput: 'followOutput',
        itemContent: 'itemContent',
        groupContent: 'groupContent',
        overscan: 'overscan',
        increaseViewportBy: 'increaseViewportBy',
        totalCount: 'totalCount',
        groupCounts: 'groupCounts',
        topItemCount: 'topItemCount',
        firstItemIndex: 'firstItemIndex',
        initialTopMostItemIndex: 'initialTopMostItemIndex',
        components: 'components',
        atBottomThreshold: 'atBottomThreshold',
        atTopThreshold: 'atTopThreshold',
        computeItemKey: 'computeItemKey',
        defaultItemHeight: 'defaultItemHeight',
        fixedItemHeight: 'fixedItemHeight',
        itemSize: 'itemSize',
        scrollSeekConfiguration: 'scrollSeekConfiguration',
        headerFooterTag: 'headerFooterTag',
        data: 'data',
        initialItemCount: 'initialItemCount',
        initialScrollTop: 'initialScrollTop',
        alignToBottom: 'alignToBottom',
        useWindowScroll: 'useWindowScroll',
        customScrollParent: 'customScrollParent',
        scrollerRef: 'scrollerRef',
        logLevel: 'logLevel',
    },
    methods: {
        scrollToIndex: 'scrollToIndex',
        scrollIntoView: 'scrollIntoView',
        scrollTo: 'scrollTo',
        scrollBy: 'scrollBy',
        autoscrollToBottom: 'autoscrollToBottom',
        getState: 'getState',
    },
    events: {
        isScrolling: 'isScrolling',
        endReached: 'endReached',
        startReached: 'startReached',
        rangeChanged: 'rangeChanged',
        atBottomStateChange: 'atBottomStateChange',
        atTopStateChange: 'atTopStateChange',
        totalListHeightChanged: 'totalListHeightChanged',
        itemsRendered: 'itemsRendered',
        groupIndices: 'groupIndices',
    },
}, ListRoot), _a.Component), usePublisher = _a.usePublisher, useEmitterValue = _a.useEmitterValue, useEmitter = _a.useEmitter;
var Scroller = /*#__PURE__*/ buildScroller({ usePublisher: usePublisher, useEmitterValue: useEmitterValue, useEmitter: useEmitter });
var WindowScroller = /*#__PURE__*/ buildWindowScroller({ usePublisher: usePublisher, useEmitterValue: useEmitterValue, useEmitter: useEmitter });
export var Virtuoso = List;
export var GroupedVirtuoso = List;
